import type { EntityDto, TreeRelationDto } from "api/types";

import type { Edge, PeerPair } from "./treeGraph.types";

/** Couples: the strongest same-row claim, settled before any weaker peer tie. */
const COUPLE_TYPES = new Set(["Husband", "Wife", "LoveInterest"]);
/** Recorded siblings. Shared-parent siblings need no rule — their parent edges already align them. */
const SIBLING_TYPES = new Set(["Brother", "Sister"]);
/** Symmetric ties between contemporaries: whoever you befriend, fight or travel with is your peer. */
const SOCIAL_TYPES = new Set([
	"Friend",
	"Enemy",
	"Rival",
	"AlliedWith",
	"BondedTo",
	"BondedWith",
	"CompanionOf",
	"HasCompanion"
]);

/**
 * Pairs a relation says belong on the same generation row, strongest claim first: couples, then
 * recorded siblings, then the symmetric social ties. Ordering matters because peer claims can
 * contradict each other and only some of them survive (see assignLevels) — a marriage should win
 * the row, a shared enemy should yield.
 *
 * Organization membership is deliberately absent. A roster is not a generation: this tree's own
 * Twelve Olympians holds Zeus, his children and his aunt Aphrodite, and demanding that they share
 * a row is what used to collapse three generations onto one. Membership still pulls teammates
 * together horizontally through the adjacency seating, and a tree that records nothing but a
 * roster still lands its members on one row, because nothing pushes them apart.
 */
export const buildPeerPairs = (relations: TreeRelationDto[]): PeerPair[] => {
	const couples: PeerPair[] = [];
	const siblings: PeerPair[] = [];
	const social: PeerPair[] = [];

	relations.forEach((relation: TreeRelationDto) => {
		const pair: PeerPair = { aId: relation.fromEntityId, bId: relation.toEntityId };
		if (COUPLE_TYPES.has(relation.relationType)) {
			couples.push(pair);
		} else if (SIBLING_TYPES.has(relation.relationType)) {
			siblings.push(pair);
		} else if (SOCIAL_TYPES.has(relation.relationType)) {
			social.push(pair);
		}
	});

	return [...couples, ...siblings, ...social];
};

/** Disjoint-set forest over entity ids: each set is one generation row's worth of peers. */
type PeerGroups = {
	find: (id: string) => string;
	union: (aId: string, bId: string) => void;
};

const createPeerGroups = (ids: string[]): PeerGroups => {
	const parent = new Map<string, string>(ids.map((id: string) => [id, id]));

	const find = (id: string): string => {
		if (!parent.has(id)) {
			return id;
		}

		let root = id;
		while (parent.get(root) !== root) {
			root = parent.get(root)!;
		}

		let cursor = id;
		while (parent.get(cursor) !== root) {
			const next = parent.get(cursor)!;
			parent.set(cursor, root);
			cursor = next;
		}

		return root;
	};

	return {
		find,
		union: (aId: string, bId: string): void => {
			parent.set(find(aId), find(bId));
		}
	};
};

/**
 * Drop the edges that close a loop in the parent→child graph, keeping every other edge.
 *
 * A loop has no consistent set of generations — someone would have to sit below themselves — and
 * recorded data does close them (a mis-entered direction is enough). Depth-first search names the
 * offending edge precisely, so one bad row costs one edge instead of scrambling the whole chart.
 */
const removeCycleEdges = (ids: string[], edges: Edge[]): Edge[] => {
	const outgoing = new Map<string, Edge[]>();
	edges.forEach((edge: Edge) => {
		if (!outgoing.has(edge.parentId)) {
			outgoing.set(edge.parentId, []);
		}
		outgoing.get(edge.parentId)!.push(edge);
	});

	const onStack = new Set<string>();
	const visited = new Set<string>();
	const dropped = new Set<Edge>();

	// Iterative: a deep lineage would overflow the call stack on a recursive walk.
	ids.forEach((rootId: string) => {
		if (visited.has(rootId)) {
			return;
		}

		visited.add(rootId);
		onStack.add(rootId);
		const stack: { id: string; nextChild: number }[] = [{ id: rootId, nextChild: 0 }];
		while (stack.length > 0) {
			const frame = stack[stack.length - 1];
			const children = outgoing.get(frame.id) ?? [];
			if (frame.nextChild >= children.length) {
				onStack.delete(frame.id);
				stack.pop();

				continue;
			}

			const edge = children[frame.nextChild];
			frame.nextChild += 1;
			if (onStack.has(edge.childId)) {
				dropped.add(edge);
			} else if (!visited.has(edge.childId)) {
				visited.add(edge.childId);
				onStack.add(edge.childId);
				stack.push({ id: edge.childId, nextChild: 0 });
			}
		}
	});

	return edges.filter((edge: Edge) => !dropped.has(edge));
};

/** Parent→child edges rewritten between peer groups, with edges inside one group left out. */
const contractEdges = (edges: Edge[], find: (id: string) => string): Map<string, string[]> => {
	const outgoing = new Map<string, string[]>();
	edges.forEach((edge: Edge) => {
		const parentGroup = find(edge.parentId);
		const childGroup = find(edge.childId);
		if (parentGroup === childGroup) {
			return;
		}

		if (!outgoing.has(parentGroup)) {
			outgoing.set(parentGroup, []);
		}
		outgoing.get(parentGroup)!.push(childGroup);
	});

	return outgoing;
};

/** Every group reachable by walking parent→child from the given one. */
const descendantsOf = (outgoing: Map<string, string[]>, groupId: string): Set<string> => {
	const seen = new Set<string>([groupId]);
	const queue: string[] = [groupId];
	for (let head = 0; head < queue.length; head += 1) {
		(outgoing.get(queue[head]) ?? []).forEach((nextId: string) => {
			if (!seen.has(nextId)) {
				seen.add(nextId);
				queue.push(nextId);
			}
		});
	}

	return seen;
};

/**
 * Assign a generation level to every entity: parents strictly above their children, peers on the
 * same row, and everything the tree only connects sideways seated with what it connects to.
 *
 * Peer ties and parent edges can contradict each other — this tree marries Hades to Persephone,
 * his own niece — so they are not treated as equals. Parent edges are absolute: the layering is a
 * longest-path over them, which puts every child strictly below every one of their parents. Peer
 * ties are then honoured one at a time, strongest first, and a tie is dropped the moment granting
 * it would seat someone level with their own ancestor or descendant. That is what makes the result
 * well-defined: the previous relaxation loop treated both as equals, so a single contradiction
 * (any organization holding both a parent and their child) ratcheted every level in the cycle
 * upward until the iteration cap cut it off, leaving whatever half-finished numbering it had
 * reached — generations collapsed together, and children drawn above their parents.
 */
export const assignLevels = (
	entities: EntityDto[],
	hierarchy: Edge[],
	peers: PeerPair[],
	adjacency: Map<string, string[]>
): Map<string, number> => {
	const ids = entities.map((entity: EntityDto) => entity.id);
	const known = new Set<string>(ids);
	const edges = removeCycleEdges(
		ids,
		hierarchy.filter(
			(edge: Edge) => edge.parentId !== edge.childId && known.has(edge.parentId) && known.has(edge.childId)
		)
	);

	// Peers are merged into one row only where the parent edges leave room for it. Each merge is
	// checked against the contracted graph as it stands, so accepting one tie never invalidates
	// another: an ancestor and a descendant are never pulled onto the same row.
	const groups = createPeerGroups(ids);
	peers.forEach((peer: PeerPair) => {
		if (!known.has(peer.aId) || !known.has(peer.bId)) {
			return;
		}

		const aGroup = groups.find(peer.aId);
		const bGroup = groups.find(peer.bId);
		if (aGroup === bGroup) {
			return;
		}

		const outgoing = contractEdges(edges, groups.find);
		if (descendantsOf(outgoing, aGroup).has(bGroup) || descendantsOf(outgoing, bGroup).has(aGroup)) {
			return;
		}

		groups.union(aGroup, bGroup);
	});

	// Longest path over the contracted graph: a row sits one below the deepest row feeding it.
	const outgoing = contractEdges(edges, groups.find);
	const groupIds = [...new Set(ids.map((id: string) => groups.find(id)))];
	const remainingParents = new Map<string, number>(groupIds.map((groupId: string) => [groupId, 0]));
	outgoing.forEach((children: string[]) => {
		children.forEach((childId: string) => remainingParents.set(childId, (remainingParents.get(childId) ?? 0) + 1));
	});

	const groupLevel = new Map<string, number>(groupIds.map((groupId: string) => [groupId, 0]));
	const sources = groupIds.filter((groupId: string) => remainingParents.get(groupId) === 0);
	const queue = [...sources];
	for (let head = 0; head < queue.length; head += 1) {
		const groupId = queue[head];
		(outgoing.get(groupId) ?? []).forEach((childId: string) => {
			groupLevel.set(childId, Math.max(groupLevel.get(childId) ?? 0, groupLevel.get(groupId)! + 1));
			remainingParents.set(childId, remainingParents.get(childId)! - 1);
			if (remainingParents.get(childId) === 0) {
				queue.push(childId);
			}
		});
	}

	// A row with no ancestry of its own hangs at the top by default. Drop it to just above its
	// earliest child instead, so a consort recorded without parents — Alcmene, Semele, Danaë — sits
	// with the generation they married into rather than alone above the whole tree. Only rows
	// without parents move, and nothing that moves is anyone's child, so no edge is inverted.
	sources.forEach((groupId: string) => {
		const children = outgoing.get(groupId) ?? [];
		if (children.length === 0) {
			return;
		}

		const earliestChild = Math.min(...children.map((childId: string) => groupLevel.get(childId) ?? 0));
		if (earliestChild - 1 > groupLevel.get(groupId)!) {
			groupLevel.set(groupId, earliestChild - 1);
		}
	});

	// Everything the tree records no ancestry for — places, objects, events, creatures, and every
	// person known only by who they stood beside — takes the row of whatever it connects to, so a
	// home lands with its household and a weapon with the hand that carries it. Anchors spread one
	// hop per pass; whatever connects to nothing keeps the top row.
	const anchored = new Set<string>();
	edges.forEach((edge: Edge) => {
		anchored.add(groups.find(edge.parentId));
		anchored.add(groups.find(edge.childId));
	});

	const groupNeighbours = new Map<string, Set<string>>();
	adjacency.forEach((neighbours: string[], id: string) => {
		if (!known.has(id)) {
			return;
		}

		const groupId = groups.find(id);
		if (!groupNeighbours.has(groupId)) {
			groupNeighbours.set(groupId, new Set<string>());
		}
		neighbours.forEach((otherId: string) => {
			if (known.has(otherId) && groups.find(otherId) !== groupId) {
				groupNeighbours.get(groupId)!.add(groups.find(otherId));
			}
		});
	});

	const floating = groupIds.filter((groupId: string) => !anchored.has(groupId));
	for (let pass = 0; pass < floating.length; pass += 1) {
		const settled: [string, number][] = [];
		floating.forEach((groupId: string) => {
			if (anchored.has(groupId)) {
				return;
			}

			const neighbourLevels = [...(groupNeighbours.get(groupId) ?? new Set<string>())]
				.filter((otherId: string) => anchored.has(otherId))
				.map((otherId: string) => groupLevel.get(otherId)!);
			if (neighbourLevels.length > 0) {
				const total = neighbourLevels.reduce((sum: number, value: number) => sum + value, 0);
				settled.push([groupId, Math.round(total / neighbourLevels.length)]);
			}
		});
		if (settled.length === 0) {
			break;
		}

		settled.forEach(([groupId, value]: [string, number]) => {
			groupLevel.set(groupId, value);
			anchored.add(groupId);
		});
	}

	return new Map<string, number>(ids.map((id: string) => [id, groupLevel.get(groups.find(id)) ?? 0]));
};
